// Push Notification Subscription Utilities
import { storePushSubscription, removePushSubscription } from './pushNotifications.js';

// VAPID public key (must match the one in your Firebase Functions)
const VAPID_PUBLIC_KEY = 'BMU8SuL5VJINa1mKf4QcWpIpjoqQgMDyzOzZMpUT9h0tAzRLFZNV9hpCH1kH0ovVa-_HRWvFScJXDcZypX4b1GU';

/**
 * Convert VAPID public key to Uint8Array for web-push
 * @param {string} base64String - The base64 VAPID public key
 * @returns {Uint8Array} The converted key
 */
function urlBase64ToUint8Array(base64String) {
  try {
    // Validate input
    if (!base64String || typeof base64String !== 'string') {
      throw new Error('Invalid input: VAPID key must be a non-empty string');
    }
    
    // Remove any whitespace and ensure proper padding
    const cleanKey = base64String.trim();
    if (cleanKey.length === 0) {
      throw new Error('VAPID key cannot be empty');
    }
    
    // Add proper padding for base64
    const padding = '='.repeat((4 - cleanKey.length % 4) % 4);
    const base64 = (cleanKey + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    // Convert to binary
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    
    // Validate the output array
    if (outputArray.length === 0) {
      throw new Error('Generated array is empty');
    }
    
    // Log successful conversion for debugging
    console.log('🔑 VAPID key converted successfully:', {
      originalLength: base64String.length,
      cleanedLength: cleanKey.length,
      outputLength: outputArray.length,
      firstBytes: Array.from(outputArray.slice(0, 4)),
      lastBytes: Array.from(outputArray.slice(-4))
    });
    
    return outputArray;
  } catch (error) {
    console.error('❌ Error converting VAPID key:', error);
    console.error('🔑 VAPID key that failed:', base64String);
    throw new Error(`Invalid VAPID public key: ${error.message}`);
  }
}

export function isIosBrowser() {
  if (typeof navigator === 'undefined') return false;
  if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) return true;
  // iPadOS 13+ desktop mode: "MacIntel" + touch
  return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
}

/**
 * Installed PWA / “Add to Home Screen” context (required for Web Push on iOS Safari).
 * WebKit may report fullscreen or legacy navigator.standalone instead of standalone.
 */
export function isStandalonePWA() {
  if (typeof window === 'undefined') return false;
  if (typeof navigator !== 'undefined' && navigator.standalone === true) {
    return true;
  }
  const queries = [
    '(display-mode: standalone)',
    '(display-mode: fullscreen)',
  ];
  for (const q of queries) {
    try {
      if (window.matchMedia?.(q)?.matches) return true;
    } catch {
      /* ignore */
    }
  }
  return false;
}

/**
 * Sync hint only — can be wrong on iOS PWA where PushManager is not on window.
 * Prefer probePushSubscriptionSupport() for Alerts / subscribe flows.
 * @returns {'ready'|'ios_install'|'insecure'|'no_sw'|'unsupported'}
 */
export function getPushSetupHint() {
  if (typeof window === 'undefined') return 'unsupported';
  if (!window.isSecureContext) return 'insecure';
  if (!('serviceWorker' in navigator)) return 'no_sw';

  const ios = isIosBrowser();
  const standalone = isStandalonePWA();
  if (ios && !standalone) {
    return 'ios_install';
  }

  if (!('PushManager' in window)) {
    return 'unsupported';
  }

  return 'ready';
}

/**
 * Authoritative async check. On iOS home-screen apps WebKit often omits window.PushManager
 * but still exposes registration.pushManager (see WebKit home screen web app docs).
 * @returns {{ hint: 'ready'|'ios_install'|'insecure'|'no_sw'|'unsupported'|'ios_push_unavailable', available: boolean }}
 */
export async function probePushSubscriptionSupport() {
  if (typeof window === 'undefined') {
    return {hint: 'unsupported', available: false};
  }
  if (!window.isSecureContext) {
    return {hint: 'insecure', available: false};
  }
  if (!('serviceWorker' in navigator)) {
    return {hint: 'no_sw', available: false};
  }

  const ios = isIosBrowser();
  const standalone = isStandalonePWA();
  if (ios && !standalone) {
    return {hint: 'ios_install', available: false};
  }

  try {
    let reg = await navigator.serviceWorker.getRegistration('/');
    if (!reg) {
      reg = await navigator.serviceWorker.register('/sw.js', {scope: '/'});
    }
    await reg.update();
    const ready = await navigator.serviceWorker.ready;
    if (ready?.pushManager) {
      return {hint: 'ready', available: true};
    }
  } catch (e) {
    console.warn('probePushSubscriptionSupport:', e);
  }

  if (typeof window !== 'undefined' && 'PushManager' in window) {
    return {hint: 'ready', available: true};
  }

  if (ios && standalone) {
    return {
      hint: 'ios_push_unavailable',
      available: false,
    };
  }

  return {hint: 'unsupported', available: false};
}

/**
 * Check if push notifications are supported in this context
 * @returns {boolean} True if supported (best-effort sync; iOS PWA may need async probe)
 */
export function isPushNotificationSupported() {
  return getPushSetupHint() === 'ready';
}

/**
 * Check if push notifications are permitted
 * @returns {Promise<string>} The permission status
 */
export async function getPushNotificationPermission() {
  const probe = await probePushSubscriptionSupport();
  if (!probe.available) {
    return 'not-supported';
  }

  return await Notification.permission;
}

/**
 * Request permission for push notifications
 * @returns {Promise<string>} The permission status
 */
export async function requestPushNotificationPermission() {
  const probe = await probePushSubscriptionSupport();
  if (!probe.available) {
    const msg = probe.hint === 'ios_install' ?
      'Add YSR to your Home Screen, open it from the icon, then enable notifications.' :
      'Push notifications are not supported in this browser.';
    throw new Error(msg);
  }

  try {
    // Check current permission first
    let currentPermission = Notification.permission;
    console.log('🔔 Current permission status:', currentPermission);
    
    // Detect if we're on desktop (not mobile)
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const isDesktop = !isMobile;
    
    if (currentPermission === 'denied') {
      // If already denied, we need to guide the user
      console.log('⚠️ Permission already denied - user must manually enable');
      
      // Only show alert on mobile, not on desktop
      if (!isDesktop) {
        // Show helpful message to user
        const message = 'Push notifications are blocked. Please:\n\n' +
          '1. Click the notification icon in your browser\'s address bar\n' +
          '2. Or go to browser settings and allow notifications for this site\n' +
          '3. Refresh the page and try again';
        
        alert(message);
      } else {
        console.log('🔔 Desktop detected - suppressing notification permission popup');
      }
      return 'denied';
    }
    
    // Request permission
    const permission = await Notification.requestPermission();
    console.log('🔔 Push notification permission result:', permission);
    
    if (permission === 'denied') {
      console.log('⚠️ Permission denied by user');
      // Only show alert on mobile, not on desktop
      if (!isDesktop) {
        alert('Permission denied. You can enable notifications later in your browser settings.');
      } else {
        console.log('🔔 Desktop detected - suppressing notification denial popup');
      }
    }
    
    return permission;
  } catch (error) {
    console.error('❌ Error requesting push notification permission:', error);
    throw error;
  }
}

/**
 * Subscribe to push notifications
 * @param {string} userId - The user's ID
 * @returns {Promise<Object>} The subscription object
 */
export async function subscribeToPushNotifications(userId) {
  const probe = await probePushSubscriptionSupport();
  if (!probe.available) {
    const msg = probe.hint === 'ios_install' ?
      'Add YSR to your Home Screen, open it from the icon, then try again.' :
      'Push notifications are not supported in this browser.';
    throw new Error(msg);
  }

  try {
    // Check permission first
    const permission = await getPushNotificationPermission();
    if (permission !== 'granted') {
      const newPermission = await requestPushNotificationPermission();
      if (newPermission !== 'granted') {
        throw new Error('Push notification permission denied');
      }
    }

    let reg = await navigator.serviceWorker.getRegistration('/');
    if (!reg) {
      await navigator.serviceWorker.register('/sw.js', { scope: '/' });
    }
    reg = await navigator.serviceWorker.ready;
    await reg.update();
    console.log('✅ Service Worker ready');

    console.log('🔑 Converting VAPID key...');
    const applicationServerKey = urlBase64ToUint8Array(VAPID_PUBLIC_KEY);
    console.log('🔑 VAPID key converted, length:', applicationServerKey.length);

    let subscription = await reg.pushManager.getSubscription();
    if (!subscription) {
      subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey
      });
    }

    // Force get the keys from the subscription
    let subscriptionWithKeys = subscription;
    
    // If keys are missing, try to get them from toJSON
    if (!subscription.keys || !subscription.keys.p256dh || !subscription.keys.auth) {
      if (subscription.toJSON) {
        const jsonSub = subscription.toJSON();
        
        if (jsonSub.keys && jsonSub.keys.p256dh && jsonSub.keys.auth) {
          subscriptionWithKeys = {
            endpoint: subscription.endpoint,
            keys: {
              p256dh: jsonSub.keys.p256dh,
              auth: jsonSub.keys.auth
            },
            expirationTime: subscription.expirationTime
          };
        }
      }
    }

    // Store the subscription with keys
    await storePushSubscription(userId, subscriptionWithKeys);

    return subscriptionWithKeys;

  } catch (error) {
    console.error('❌ Error subscribing to push notifications:', error);
    throw error;
  }
}

/**
 * Unsubscribe from push notifications
 * @param {string} userId - The user's ID
 * @returns {Promise<boolean>} True if successful
 */
export async function unsubscribeFromPushNotifications(userId) {
  const probe = await probePushSubscriptionSupport();
  if (!probe.available) {
    return false;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    if (subscription) {
      await subscription.unsubscribe();
      console.log('✅ Push subscription removed');
    }

    // Remove from storage
    await removePushSubscription(userId);

    return true;

  } catch (error) {
    console.error('❌ Error unsubscribing from push notifications:', error);
    return false;
  }
}

/**
 * Get current push subscription
 * @returns {Promise<Object|null>} The current subscription or null
 */
export async function getCurrentPushSubscription() {
  const probe = await probePushSubscriptionSupport();
  if (!probe.available) {
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return subscription;
  } catch (error) {
    console.error('❌ Error getting current push subscription:', error);
    return null;
  }
}

/**
 * Check if user is subscribed to push notifications
 * @returns {Promise<boolean>} True if subscribed
 */
export async function isSubscribedToPushNotifications() {
  const subscription = await getCurrentPushSubscription();
  return subscription !== null;
}

/**
 * Initialize push notifications for a user
 * @param {string} userId - The user's ID
 * @returns {Promise<Object>} The subscription object
 */
export async function initializePushNotifications(userId) {
  try {
    // Check if already subscribed
    const isSubscribed = await isSubscribedToPushNotifications();
    
    if (isSubscribed) {
      console.log('✅ User already subscribed to push notifications');
      const subscription = await getCurrentPushSubscription();
      
      // Ensure subscription is stored
      await storePushSubscription(userId, subscription);
      
      return subscription;
    } else {
      // Subscribe for the first time
      console.log('🆕 Subscribing user to push notifications for the first time');
      return await subscribeToPushNotifications(userId);
    }
  } catch (error) {
    console.error('❌ Error initializing push notifications:', error);
    throw error;
  }
}

/**
 * Test push notification subscription
 * @param {string} userId - The user's ID
 * @returns {Promise<boolean>} True if test successful
 */
export async function testPushNotificationSubscription(userId) {
  try {
    const subscription = await getCurrentPushSubscription();
    
    if (!subscription) {
      console.log('❌ No push subscription found');
      return false;
    }

    // Send test notification via service worker message
    if (navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'TEST_PUSH',
        data: {
          title: 'Test Push Notification! 🧪',
          body: 'This is a test notification to verify your subscription is working.',
          url: '/admin/live-dashboard'
        }
      });
      
      console.log('✅ Test notification message sent to service worker');
      return true;
    } else {
      console.log('❌ Service worker controller not available');
      return false;
    }

  } catch (error) {
    console.error('❌ Error testing push notification subscription:', error);
    return false;
  }
}
